import { useTranslation } from 'react-i18next';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { getUserToChatFromLocalStorage } from 'helpers/localStorage';
import { auth, db, ensureFirebaseSignedIn } from '../../../services/firebase';
import {
  addDoc,
  collection,
  DocumentData,
  doc,
  getDocs,
  limit,
  onSnapshot,
  orderBy,
  query,
  QueryDocumentSnapshot,
  startAfter,
  setDoc,
  updateDoc,
  where,
  Timestamp,
} from 'firebase/firestore';
import { useDispatch, useSelector } from 'react-redux';
import { setCurrentUserChat } from '../../../store/auth/authSlice';
import { RootState } from '../../../types';
import { v4 as uuid } from 'uuid';
import { TChat } from '../../../types/types';
import { CGroup, CUser } from '../../../types/consultant/consultant';
import { axiosClient } from '../../../api/axiosClient';
import { sendEmailForConsultant } from '../Layout/SideBar';
import { sortGroupChat } from '../../../utils/helper';
import { nameToHanas } from '../../../helpers/constant';
import { GroupedItem, Item } from './chatType';

type ChatMessage = {
  id: string;
  [key: string]: any;
};

const GROUP_PAGE_SIZE = 20;
const MESSAGE_PAGE_SIZE = 50;

const useServices = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(false);
  const currentUser = getUserToChatFromLocalStorage();
  const [listUsers, setListUsers] = useState<Array<CUser>>([]);
  const [listDataGroup, setListDataGroup] = useState<Array<CGroup>>([]);
  const [listDataGroupSorted, setListDataGroupSorted] = useState<Array<any>>([]);
  const [listDirectChat, setListDirectChat] = useState<Array<CGroup>>([]);
  const [listMessage, setListMessage] = useState<ChatMessage[]>([]);
  const [isLoadingChatroom, setIsLoadingChatroom] = useState<boolean>(false);
  const [messagePageSize, setMessagePageSize] = useState<number>(MESSAGE_PAGE_SIZE);
  const [hasMoreMessages, setHasMoreMessages] = useState<boolean>(true);
  const [isBumpingMessagePageSize, setIsBumpingMessagePageSize] = useState<boolean>(false);
  const [userChat, setUserChat] = useState<any>();
  const [groupChat, setGroupChat] = useState<any>();
  const [textInput, setTextInput] = useState('');
  const { currentUserChat } = useSelector((state: RootState) => state.auth);
  const [isShowCreateChat, setIsShowCreateChat] = useState(false);
  const [isShowBtn, setIsShowBtn] = useState(false);
  const [showCreateChat, setShowCreateChat] = useState<TChat>('');
  const [isEdit, setIsEdit] = useState(false);
  const [idEdit, setIdEdit] = useState('');
  const [listUserNew, setListUserNew] = useState<Array<CUser>>([]);
  const [comGroups, setComGroups] = useState<any[]>([]);
  const [groupPageCursor, setGroupPageCursor] =
    useState<QueryDocumentSnapshot<DocumentData> | null>(null);
  const [hasMoreGroups, setHasMoreGroups] = useState<boolean>(true);
  const [isLoadingMoreGroups, setIsLoadingMoreGroups] = useState<boolean>(false);
  const [readReceiptsByChatroom, setReadReceiptsByChatroom] = useState<Record<string, any>>({});
  const [unreadsByChatroom, setUnreadsByChatroom] = useState<Record<string, number>>({});
  const [unreadMessageAtByChatroom, setUnreadMessageAtByChatroom] = useState<
    Record<string, number>
  >({});
  const [hasLoadedUserUnreads, setHasLoadedUserUnreads] = useState(false);
  const [activePrimaryMemberId, setActivePrimaryMemberId] = useState<string | null>(null);
  const [primaryMemberReadReceiptsByChatroom, setPrimaryMemberReadReceiptsByChatroom] = useState<
    Record<string, any>
  >({});
  const getMembersInfoArray = (membersInfo: any) => {
    if (Array.isArray(membersInfo)) return membersInfo;
    if (membersInfo && typeof membersInfo === 'object') return Object.values(membersInfo);
    return [];
  };

  const getChatroomId = (chat: any) => chat?.uid ?? chat?.id;

  const getActiveChatroomId = () => getChatroomId(groupChat) ?? getChatroomId(userChat);

  const clearUnreadForChatroom = (chatroomId?: string) => {
    if (!chatroomId) return;
    setUnreadsByChatroom((prev) => {
      if (!(chatroomId in prev)) return prev;
      const next = { ...prev };
      delete next[chatroomId];
      return next;
    });
  };

  const getPrimaryMemberIdByChatroom = (chatroomId?: string) => {
    if (!chatroomId) return null;
    const roomMeta: any =
      listDataGroup.find((item) => getChatroomId(item) === chatroomId) ||
      (getChatroomId(groupChat) === chatroomId ? groupChat : null) ||
      (getChatroomId(userChat) === chatroomId ? userChat : null);
    return roomMeta?.primaryMemberId ?? null;
  };

  const hasImageAttachments = (attachments: any[] = []) => {
    return attachments.some(
      (attachment: any) =>
        attachment?.type === 'image' &&
        typeof attachment?.url === 'string' &&
        attachment.url.trim().length > 0,
    );
  };

  const enrichMessagesWithSenderRole = (messages: any[], membersInfoSource?: any) => {
    const roleBySender = new Map<string, string>();
    const membersInfo = getMembersInfoArray(membersInfoSource);
    membersInfo.forEach((member: any) => {
      if (member?.uid && member?.role) {
        roleBySender.set(member.uid, member.role);
      }
    });
    listUserNew.forEach((user: any) => {
      if (user?.uid && user?.role && !roleBySender.has(user.uid)) {
        roleBySender.set(user.uid, user.role);
      }
    });

    return messages.map((message: any) => {
      if (message?.senderRole || !message?.senderId) return message;
      const senderRole = roleBySender.get(message.senderId);
      return senderRole ? { ...message, senderRole } : message;
    });
  };

  const getItemSortTime = (item: any) =>
    item?.lastMessageAt ??
    item?.updatedAt ??
    item?.updateAt ??
    item?.createdAt ??
    item?.createAt ??
    0;
  const getChatroomSortTime = (room: any) =>
    Math.max(
      Number(unreadMessageAtByChatroom[getChatroomId(room)] ?? 0),
      Number(
        room?.lastMessageAt ??
          room?.updatedAt ??
          room?.updateAt ??
          room?.createdAt ??
          room?.createAt ??
          0,
      ),
    );

  const buildFallbackEmployeeGroupName = (room: any) => {
    const explicitName = String(room?.nameGroupChat ?? '').trim();
    if (explicitName) return explicitName;
    const clientLabel = String(room?.clientName ?? room?.clientId ?? '').trim();
    const facilityLabel = String(room?.facilityName ?? room?.facilityId ?? '').trim();
    if (clientLabel && facilityLabel) return `${clientLabel} - ${facilityLabel}`;
    if (clientLabel) return clientLabel;
    if (facilityLabel) return facilityLabel;
    return '未分類';
  };

  function processArray(array: any): (Item | GroupedItem)[] {
    const groupMap: { [key: string]: Item[] } = {};
    const result: (Item | GroupedItem)[] = [];
    const consultantGroup: Item[] = [];

    array.forEach((item) => {
      item.updateAt = getItemSortTime(item);
      const membersInfoArray = getMembersInfoArray(item.membersInfo);
      if (item.nameGroupChat) {
        if (!groupMap[item.nameGroupChat]) {
          groupMap[item.nameGroupChat] = [];
        }
        groupMap[item.nameGroupChat].push(item);
      } else if (
        ['custom', 'interviewer'].includes((item as any).category) ||
        membersInfoArray.find((member) => member.role === 'consultant') ||
        item.type === 'consultant'
      ) {
        consultantGroup.push(item);
      } else {
        result.push(item);
      }
    });

    for (const groupName in groupMap) {
      const groupItems = groupMap[groupName];
      groupItems.sort((a, b) => getItemSortTime(b) - getItemSortTime(a));
      const maxUpdateAt = groupItems[0].updateAt;
      const newGroupItem: GroupedItem = {
        nameGroupChat: groupName,
        updateAt: maxUpdateAt,
        items: groupItems,
      };
      result.push(newGroupItem);
    }

    if (consultantGroup.length > 0) {
      consultantGroup.sort((a, b) => getItemSortTime(b) - getItemSortTime(a));
      const maxUpdateAt = consultantGroup[0].updateAt;
      const consultantGroupItem: GroupedItem = {
        nameGroupChat: '面談員',
        updateAt: maxUpdateAt,
        items: consultantGroup,
      };
      result.push(consultantGroupItem);
    }

    result.sort((a: any, b: any) => {
      const aHasNameGroup = Boolean(a?.nameGroupChat);
      const bHasNameGroup = Boolean(b?.nameGroupChat);
      if (aHasNameGroup !== bHasNameGroup) {
        return aHasNameGroup ? -1 : 1;
      }
      return getItemSortTime(b) - getItemSortTime(a);
    });
    return result;
  }

  useEffect(() => {
    if (currentUserChat?.role !== 'com' || !currentUserChat?.uid) {
      setComGroups([]);
      setGroupPageCursor(null);
      setHasMoreGroups(false);
      setIsLoadingMoreGroups(false);
      return;
    }

    const groupsQuery = query(
      collection(db, 'group'),
      where('members', 'array-contains', currentUserChat.uid),
      where('active', '==', true),
      orderBy('lastMessageAt', 'desc'),
      limit(GROUP_PAGE_SIZE),
    );

    const unSub = onSnapshot(
      groupsQuery,
      (snapshot) => {
        const groups = snapshot.docs.map((snapshotDoc) => ({
          id: snapshotDoc.id,
          ...(snapshotDoc.data() as any),
        }));
        setComGroups(groups);
        setGroupPageCursor(snapshot.docs[snapshot.docs.length - 1] ?? null);
        setHasMoreGroups(snapshot.docs.length === GROUP_PAGE_SIZE);
        setIsLoadingMoreGroups(false);
      },
      () => {
        setComGroups([]);
        setGroupPageCursor(null);
        setHasMoreGroups(false);
        setIsLoadingMoreGroups(false);
      },
    );

    return () => {
      unSub();
    };
  }, [currentUserChat?.role, currentUserChat?.uid]);

  const loadMoreGroups = useCallback(async () => {
    if (currentUserChat?.role !== 'com') return;
    if (!currentUserChat?.uid || !groupPageCursor) return;
    if (isLoadingMoreGroups || !hasMoreGroups) return;

    setIsLoadingMoreGroups(true);
    try {
      const groupsQuery = query(
        collection(db, 'group'),
        where('members', 'array-contains', currentUserChat.uid),
        where('active', '==', true),
        orderBy('lastMessageAt', 'desc'),
        startAfter(groupPageCursor),
        limit(GROUP_PAGE_SIZE),
      );
      const snapshot = await getDocs(groupsQuery);
      const nextGroups = snapshot.docs.map((snapshotDoc) => ({
        id: snapshotDoc.id,
        ...(snapshotDoc.data() as any),
      }));
      setComGroups((prev) => {
        const mapById = new Map(prev.map((group: any) => [group.id, group]));
        nextGroups.forEach((group: any) => {
          mapById.set(group.id, group);
        });
        const orderedIds = [...prev.map((group: any) => group.id), ...nextGroups.map((g) => g.id)];
        const seen = new Set<string>();
        return orderedIds
          .filter((id) => {
            if (!id || seen.has(id)) return false;
            seen.add(id);
            return true;
          })
          .map((id) => mapById.get(id))
          .filter(Boolean);
      });
      setGroupPageCursor(snapshot.docs[snapshot.docs.length - 1] ?? null);
      setHasMoreGroups(snapshot.docs.length === GROUP_PAGE_SIZE);
    } catch (_) {
      setHasMoreGroups(false);
    } finally {
      setIsLoadingMoreGroups(false);
    }
  }, [
    currentUserChat?.role,
    currentUserChat?.uid,
    groupPageCursor,
    isLoadingMoreGroups,
    hasMoreGroups,
  ]);

  const chatroomLastMessageAtKey = useMemo(() => {
    return listDataGroup
      .map((room: any) => `${getChatroomId(room)}:${getChatroomSortTime(room)}`)
      .sort()
      .join('|');
  }, [listDataGroup, unreadMessageAtByChatroom]);

  const comGroupsLastMessageAtKey = useMemo(() => {
    return comGroups
      .map((group: any) => `${group?.id}:${Number(group?.lastMessageAt ?? 0)}`)
      .sort()
      .join('|');
  }, [comGroups]);

  useEffect(() => {
    if (currentUserChat.role !== 'com') {
      setListDataGroupSorted(processArray(listDataGroup));
      return;
    }

    const employeeRoomsByGroupId = new Map<string, any[]>();
    const consultantRooms: any[] = [];
    const fallbackGroupedRoomsByName = new Map<string, any[]>();
    const groupById = new Map(comGroups.map((group: any) => [group.id, group]));

    listDataGroup.forEach((room: any) => {
      const membersInfoArray = getMembersInfoArray(room.membersInfo);
      const isConsultantRoom =
        ['custom', 'interviewer'].includes(room?.category) ||
        membersInfoArray.some((member: any) => member?.role === 'consultant') ||
        room?.type === 'consultant';
      if (isConsultantRoom) {
        consultantRooms.push(room);
        return;
      }

      if (room?.category !== 'employee') {
        const fallbackName = String(room?.nameGroupChat ?? '').trim() || '未分類';
        const currentFallbackRooms = fallbackGroupedRoomsByName.get(fallbackName) ?? [];
        currentFallbackRooms.push(room);
        fallbackGroupedRoomsByName.set(fallbackName, currentFallbackRooms);
        return;
      }

      const groupId = room?.groupId;
      if (!groupId) {
        const fallbackName = buildFallbackEmployeeGroupName(room);
        const currentFallbackRooms = fallbackGroupedRoomsByName.get(fallbackName) ?? [];
        currentFallbackRooms.push({
          ...room,
          nameGroupChat: fallbackName,
        });
        fallbackGroupedRoomsByName.set(fallbackName, currentFallbackRooms);
        return;
      }
      if (!groupById.has(groupId)) {
        // Keep orphaned/missing-group employee rooms visible.
        const fallbackName = buildFallbackEmployeeGroupName(room);
        const currentFallbackRooms = fallbackGroupedRoomsByName.get(fallbackName) ?? [];
        currentFallbackRooms.push({
          ...room,
          nameGroupChat: fallbackName,
        });
        fallbackGroupedRoomsByName.set(fallbackName, currentFallbackRooms);
        return;
      }

      const current = employeeRoomsByGroupId.get(groupId) ?? [];
      current.push(room);
      employeeRoomsByGroupId.set(groupId, current);
    });

    const nextGroupedList: any[] = [];
    comGroups.forEach((group: any) => {
      const rooms = [...(employeeRoomsByGroupId.get(group.id) ?? [])];
      if (!rooms.length) return;
      rooms.sort((a, b) => getChatroomSortTime(b) - getChatroomSortTime(a));
      const groupName =
        String(group?.name ?? '').trim() ||
        [group?.clientName, group?.facilityName].filter(Boolean).join(' - ') ||
        '未分類';
      const topChatroomLastMessageAt = getChatroomSortTime(rooms[0]);
      const groupLastMessageAt = Number(group?.lastMessageAt ?? 0);
      nextGroupedList.push({
        nameGroupChat: groupName,
        // Prefer group.lastMessageAt for section ordering; fallback to top chatroom
        // so UI still updates immediately while backend group sync catches up.
        updateAt: groupLastMessageAt || topChatroomLastMessageAt,
        items: rooms,
      });
    });

    // Add orphan/missing groups as grouped sections, and keep all grouped sections
    // sorted by latest chatroom activity for instant UI reorder.
    fallbackGroupedRoomsByName.forEach((rooms, fallbackName) => {
      const sortedRooms = [...rooms].sort(
        (a, b) => getChatroomSortTime(b) - getChatroomSortTime(a),
      );
      if (!sortedRooms.length) return;
      nextGroupedList.push({
        nameGroupChat: fallbackName || '未分類',
        updateAt: getChatroomSortTime(sortedRooms[0]),
        items: sortedRooms,
      });
    });
    if (consultantRooms.length > 0) {
      consultantRooms.sort((a, b) => getChatroomSortTime(b) - getChatroomSortTime(a));
      nextGroupedList.push({
        nameGroupChat: '面談員',
        updateAt: getChatroomSortTime(consultantRooms[0]),
        items: consultantRooms,
      });
    }

    nextGroupedList.sort((a, b) => Number(b?.updateAt ?? 0) - Number(a?.updateAt ?? 0));

    setListDataGroupSorted(nextGroupedList);
  }, [
    currentUserChat.role,
    comGroups,
    listDataGroup,
    comGroupsLastMessageAtKey,
    chatroomLastMessageAtKey,
  ]);

  useEffect(() => {
    checkAccount();
  }, []);

  useEffect(() => {
    const loadUsers = async () => {
      try {
        const querySnapshot = await getDocs(collection(db, 'user'));
        const list: any[] = [];
        querySnapshot.forEach((snapshotDoc) => {
          list.push(snapshotDoc.data());
        });
        setListUserNew(list);
      } catch (_) {}
    };

    loadUsers();
  }, []);

  const registerAccount = async (email: string) => {
    try {
      const q = query(collection(db, 'user'), where('email', '==', email));
      const querySnapshot = await getDocs(q);
      if (querySnapshot.docs.length > 0) {
        return;
      }

      const firebaseUser = auth.currentUser ?? (await ensureFirebaseSignedIn());
      if (firebaseUser) {
        await setDoc(doc(db, 'user', firebaseUser.uid), {
          uid: firebaseUser.uid,
          displayName: currentUser.name,
          email: currentUser.email,
          avatar: 'https://ps.w.org/user-avatar-reloaded/assets/icon-128x128.png?rev=2540745',
        });
        return { user: firebaseUser };
      }
    } catch (err) {}
  };

  const login = async (_email?: string) => {
    try {
      const user = await ensureFirebaseSignedIn();
      return user ? { user } : undefined;
    } catch (err) {}
  };

  const checkAccount = async (): Promise<any> => {
    try {
      const account = await login(currentUser?.email);
      if (!account) {
        await registerAccount(currentUser?.email);
      }

      const q = query(collection(db, 'user'), where('email', '==', currentUser?.email));
      const resultUser = await getDocs(q);
      const userInfo = resultUser.docs[0].data();
      dispatch(setCurrentUserChat(userInfo));
      // get all member in app
      const listUser: any = await getListUser(userInfo);
      if (listUser) {
        setListUsers(
          listUser.docs.map((item) => ({
            ...item.data(),
            key: item?.uid,
          })),
        );
      }
    } catch (err) {}
  };

  useEffect(() => {
    if (currentUserChat.uid) {
      setIsLoading(true);
      const mapChatroomDoc = (snapshotDoc: any) => {
        const data = snapshotDoc.data();
        return {
          id: snapshotDoc.id,
          uid: data.uid ?? snapshotDoc.id,
          ...data,
          groupName: data.groupName ?? data.name,
          nameGroupChat: data.nameGroupChat,
        };
      };

      const mergeAndSortChatrooms = (employeeRooms: any[], otherRooms: any[]) => {
        const merged = [...employeeRooms, ...otherRooms];
        const uniqueById = Array.from(
          new Map(merged.map((room: any) => [room.uid ?? room.id, room])).values(),
        );
        return sortGroupChat(uniqueById);
      };

      let useFallbackQuery = false;
      let strictEmployeeRooms: any[] = [];
      let strictOtherRooms: any[] = [];
      let receivedEmployeeSnapshot = false;
      let receivedOtherSnapshot = false;
      let unSubStrictEmployee: any = null;
      let unSubStrictOther: any = null;
      let unSubFallback: any = null;

      const applyStrictResult = () => {
        if (useFallbackQuery) return;
        if (!receivedEmployeeSnapshot || !receivedOtherSnapshot) return;
        const mergedRooms = mergeAndSortChatrooms(strictEmployeeRooms, strictOtherRooms);
        setListDataGroup(mergedRooms);
        setListDirectChat([]);
        setIsLoading(false);
      };

      const startFallbackQuery = () => {
        if (useFallbackQuery) return;
        useFallbackQuery = true;
        if (unSubStrictEmployee) unSubStrictEmployee();
        if (unSubStrictOther) unSubStrictOther();

        const broadQuery = query(
          collection(db, 'chatroom'),
          where('active', '==', true),
          where('members', 'array-contains', currentUserChat.uid),
        );
        unSubFallback = onSnapshot(
          broadQuery,
          (snapshot) => {
            const listChatrooms = snapshot.docs
              .map((snapshotDoc) => mapChatroomDoc(snapshotDoc))
              .filter((chatroom: any) => chatroom?.active === true);
            setListDataGroup(sortGroupChat(listChatrooms));
            setListDirectChat([]);
            setIsLoading(false);
          },
          () => {
            setIsLoading(false);
          },
        );
      };

      const employeeQuery = query(
        collection(db, 'chatroom'),
        where('active', '==', true),
        where('category', '==', 'employee'),
        where('members', 'array-contains', currentUserChat.uid),
        orderBy('clientId'),
        orderBy('facilityId'),
        orderBy('lastMessageAt', 'desc'),
      );

      const otherCategoryQuery = query(
        collection(db, 'chatroom'),
        where('active', '==', true),
        where('category', 'in', ['interviewer', 'custom']),
        where('members', 'array-contains', currentUserChat.uid),
        orderBy('lastMessageAt', 'desc'),
      );

      unSubStrictEmployee = onSnapshot(
        employeeQuery,
        (snapshot) => {
          strictEmployeeRooms = snapshot.docs
            .map((snapshotDoc) => mapChatroomDoc(snapshotDoc))
            .filter((chatroom: any) => chatroom?.active === true);
          receivedEmployeeSnapshot = true;
          applyStrictResult();
        },
        () => {
          startFallbackQuery();
        },
      );

      unSubStrictOther = onSnapshot(
        otherCategoryQuery,
        (snapshot) => {
          strictOtherRooms = snapshot.docs
            .map((snapshotDoc) => mapChatroomDoc(snapshotDoc))
            .filter((chatroom: any) => chatroom?.active === true);
          receivedOtherSnapshot = true;
          applyStrictResult();
        },
        () => {
          startFallbackQuery();
        },
      );

      return () => {
        if (unSubStrictEmployee) unSubStrictEmployee();
        if (unSubStrictOther) unSubStrictOther();
        if (unSubFallback) unSubFallback();
      };
    }
  }, [currentUserChat.uid]);

  useEffect(() => {
    if (!currentUserChat?.uid) {
      setUnreadsByChatroom({});
      setUnreadMessageAtByChatroom({});
      setHasLoadedUserUnreads(false);
      return;
    }

    setHasLoadedUserUnreads(false);
    const userUnreadsRef = collection(db, 'userUnreads', currentUserChat.uid, 'chatrooms');
    const unreadQuery = query(userUnreadsRef, where('unreadCount', '>', 0));
    const unSub = onSnapshot(unreadQuery, (snapshot) => {
      const nextUnreads: Record<string, number> = {};
      const nextUnreadMessageAt: Record<string, number> = {};
      snapshot.docs.forEach((snapshotDoc) => {
        const data = snapshotDoc.data() as any;
        const unreadCount = Number(data?.unreadCount ?? 0);
        if (unreadCount > 0) {
          nextUnreads[snapshotDoc.id] = unreadCount;
          nextUnreadMessageAt[snapshotDoc.id] = Number(data?.lastMessageAt ?? data?.updatedAt ?? 0);
        }
      });
      setUnreadsByChatroom(nextUnreads);
      setUnreadMessageAtByChatroom((prev) => {
        const next = { ...prev };
        Object.entries(nextUnreadMessageAt).forEach(([chatroomId, messageAt]) => {
          const nextMessageAt = Number(messageAt ?? 0);
          if (nextMessageAt > Number(next[chatroomId] ?? 0)) {
            next[chatroomId] = nextMessageAt;
          }
        });
        return next;
      });
      setHasLoadedUserUnreads(true);
    });

    return () => {
      unSub();
    };
  }, [currentUserChat?.uid]);

  useEffect(() => {
    setMessagePageSize(MESSAGE_PAGE_SIZE);
    setHasMoreMessages(true);
    setIsBumpingMessagePageSize(false);
  }, [groupChat?.uid, userChat?.uid]);

  useEffect(() => {
    // Always leave edit mode when active room changes.
    setIsEdit(false);
    setIdEdit('');
    setTextInput('');
  }, [groupChat?.uid, userChat?.uid]);

  useEffect(() => {
    const activeChatroomId = getActiveChatroomId();
    if (!activeChatroomId) {
      setActivePrimaryMemberId(null);
      return;
    }

    // Use currently available room metadata immediately.
    const initialPrimaryMemberId = getPrimaryMemberIdByChatroom(activeChatroomId);
    setActivePrimaryMemberId(initialPrimaryMemberId ?? null);

    // Keep primaryMemberId synced in realtime for active room.
    const unSub = onSnapshot(doc(db, 'chatroom', activeChatroomId), (snapshot) => {
      const nextPrimaryMemberId = (snapshot.data() as any)?.primaryMemberId ?? null;
      setActivePrimaryMemberId(nextPrimaryMemberId);
    });

    return () => {
      unSub();
    };
  }, [groupChat?.uid, userChat?.uid]);

  useEffect(() => {
    const activeChatroomId = getActiveChatroomId();
    if (!activeChatroomId || !activePrimaryMemberId) {
      setPrimaryMemberReadReceiptsByChatroom({});
      return;
    }

    const unSub = onSnapshot(
      doc(db, 'chatroom', activeChatroomId, 'readReceipts', activePrimaryMemberId),
      (snapshot) => {
        setPrimaryMemberReadReceiptsByChatroom((prev) => ({
          ...prev,
          [activeChatroomId]: snapshot.exists() ? snapshot.data() : null,
        }));
      },
    );

    return () => {
      unSub();
    };
  }, [groupChat?.uid, userChat?.uid, activePrimaryMemberId]);

  const getListUser = async (userInfo) => {
    const q = query(collection(db, 'user'), where('email', '!=', userInfo.email));
    return await getDocs(q);
  };

  useEffect(() => {
    if (userChat) {
      const chatroomId = userChat?.uid ?? userChat?.id;
      if (!chatroomId) {
        setListMessage([]);
        setIsLoadingChatroom(false);
        return;
      }
      const messagesRef = collection(db, 'chatroom', chatroomId, 'messages');
      const q = query(messagesRef, orderBy('createdAt', 'desc'), limit(messagePageSize));
      const unSub = onSnapshot(
        q,
        (snapshot) => {
          const messages = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() })).reverse();
          setListMessage(enrichMessagesWithSenderRole(messages, userChat?.membersInfo));
          setHasMoreMessages(snapshot.docs.length >= messagePageSize);
          setIsBumpingMessagePageSize(false);
          setIsLoadingChatroom(false);
        },
        () => {
          setIsLoadingChatroom(false);
        },
      );

      return () => {
        unSub();
      };
    } else if (!groupChat) {
      setListMessage([]);
      setIsLoadingChatroom(false);
    }
  }, [userChat?.uid, userChat?.membersInfo, groupChat?.uid, listUserNew, messagePageSize]);

  useEffect(() => {
    if (groupChat) {
      const chatroomId = groupChat?.uid ?? groupChat?.id;
      if (!chatroomId) {
        setListMessage([]);
        setIsLoadingChatroom(false);
        return;
      }
      const messagesRef = collection(db, 'chatroom', chatroomId, 'messages');
      const q = query(messagesRef, orderBy('createdAt', 'desc'), limit(messagePageSize));
      const unSub = onSnapshot(
        q,
        (snapshot) => {
          const messages = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() })).reverse();
          setListMessage(enrichMessagesWithSenderRole(messages, groupChat?.membersInfo));
          setHasMoreMessages(snapshot.docs.length >= messagePageSize);
          setIsBumpingMessagePageSize(false);
          setIsLoadingChatroom(false);
        },
        () => {
          setIsLoadingChatroom(false);
        },
      );

      return () => {
        unSub();
      };
    } else if (!userChat) {
      setListMessage([]);
      setIsLoadingChatroom(false);
    }
  }, [groupChat?.uid, groupChat?.membersInfo, userChat?.uid, listUserNew, messagePageSize]);

  const loadMoreMessages = useCallback(() => {
    if (isBumpingMessagePageSize || !hasMoreMessages) return false;
    setIsBumpingMessagePageSize(true);
    setMessagePageSize((prev) => prev + MESSAGE_PAGE_SIZE);
    return true;
  }, [isBumpingMessagePageSize, hasMoreMessages]);

  const resetComposerEditState = () => {
    setIsEdit(false);
    setIdEdit('');
    setTextInput('');
  };

  const handlerChat = async (direct) => {
    setIsLoadingChatroom(true);
    resetComposerEditState();
    setGroupChat(null);
    setUserChat(direct);
    await Read(getChatroomId(direct), direct?.lastMessageId, direct?.lastMessageAt);
  };

  const sendMessage = async (attachmentsInput: any[] = []) => {
    // send message
    const trimmed = textInput.trim();
    if (!trimmed && attachmentsInput.length === 0) {
      return;
    }

    const chatroomId = (userChat?.uid ?? userChat?.id) || (groupChat?.uid ?? groupChat?.id);
    if (!chatroomId) {
      return;
    }

    try {
      setTextInput('');
      const nowMs = Timestamp.now().toMillis();
      const messageType = hasImageAttachments(attachmentsInput) ? 'image' : 'text';
      const createdMessageRef = await addDoc(collection(db, 'chatroom', chatroomId, 'messages'), {
        chatroomId,
        type: messageType,
        text: trimmed,
        attachments: attachmentsInput,
        senderId: currentUserChat.uid,
        senderRole: currentUserChat.role ?? null,
        senderName: currentUserChat.displayName,
        senderAvatar: currentUserChat.avatar,
        createdAt: nowMs,
        deleted: false,
      });
      await setDoc(
        doc(db, 'chatroom', chatroomId),
        {
          lastMessageId: createdMessageRef.id,
          lastMessageAt: nowMs,
          updatedAt: nowMs,
          updateAt: nowMs,
        },
        { merge: true },
      );
      await setDoc(
        doc(db, 'chatroom', chatroomId, 'readReceipts', currentUserChat.uid),
        {
          userId: currentUserChat.uid,
          chatroomId,
          lastReadMessageId: createdMessageRef.id,
          lastReadAt: nowMs,
        },
        { merge: true },
      );
      setReadReceiptsByChatroom((prev) => ({
        ...prev,
        [chatroomId]: {
          ...(prev[chatroomId] ?? {}),
          userId: currentUserChat.uid,
          chatroomId,
          lastReadMessageId: createdMessageRef.id,
          lastReadAt: nowMs,
        },
      }));
      clearUnreadForChatroom(chatroomId);
    } catch (err) {}
  };

  const deleteMessage = async (id: string) => {
    if (!listMessage || listMessage.length === 0) return;
    const chatroomId = (userChat?.uid ?? userChat?.id) || (groupChat?.uid ?? groupChat?.id);
    if (!chatroomId) return;
    const targetMessage = listMessage.find((message: any) => message.id === id);
    if (!targetMessage) return;
    if (targetMessage.deleted) return;
    if (targetMessage.senderId !== currentUserChat?.uid) return;

    try {
      await axiosClient.post(`/chatroom/${chatroomId}/messages/${id}/delete`);
    } catch (err) {}
  };

  const editMessage = async (text: string, id: string) => {
    setIsEdit(true);
    setTextInput(text);
    setIdEdit(id);
  };

  const sendEditMessage = async (attachmentsInput?: any[]) => {
    const chatroomId = (userChat?.uid ?? userChat?.id) || (groupChat?.uid ?? groupChat?.id);
    if (!chatroomId) return;
    const newText = textInput.trim();
    const editingMessage = listMessage.find((message: any) => message.id === idEdit);
    if (!editingMessage) return;
    const existingAttachments = Array.isArray(editingMessage.attachments)
      ? editingMessage.attachments
      : [];
    const nextAttachments = Array.isArray(attachmentsInput)
      ? attachmentsInput
      : existingAttachments;
    if (!newText && nextAttachments.length === 0) return;
    const messageType = hasImageAttachments(nextAttachments) ? 'image' : 'text';
    try {
      await updateDoc(doc(db, 'chatroom', chatroomId, 'messages', idEdit), {
        type: messageType,
        text: newText,
        attachments: nextAttachments,
        editedAt: Timestamp.now().toMillis(),
      });
    } catch (err) {}

    setIdEdit('');
    setIsEdit(false);
    setTextInput('');
  };

  const onCloseEditMessage = () => {
    setIsEdit(false);
    setTextInput('');
    setIdEdit('');
  };

  const handleClose = () => {
    setShowCreateChat('');
    setIsShowCreateChat(false);
  };

  const Read = useCallback(
    async (
      chatroomId,
      latestMessageIdFromChatroom?: string,
      latestMessageAtFromChatroom?: number,
    ) => {
      if (!chatroomId || !currentUserChat?.uid) return;
      const roomMeta: any = listDataGroup.find((item) => getChatroomId(item) === chatroomId);
      const latestMessage = [...listMessage]
        .filter((message) => message.deleted !== true)
        .sort((a, b) => (b.createdAt ?? 0) - (a.createdAt ?? 0))[0];
      const latestMessageId =
        latestMessage?.id ?? roomMeta?.lastMessageId ?? latestMessageIdFromChatroom;
      const latestMessageAt =
        latestMessage?.createdAt ??
        roomMeta?.lastMessageAt ??
        latestMessageAtFromChatroom ??
        Timestamp.now().toMillis();

      const existingReceipt = readReceiptsByChatroom[chatroomId];
      if (
        existingReceipt?.lastReadMessageId === (latestMessageId ?? null) &&
        (existingReceipt?.lastReadAt ?? 0) >= latestMessageAt
      ) {
        clearUnreadForChatroom(chatroomId);
        return;
      }

      try {
        await setDoc(
          doc(db, 'chatroom', chatroomId, 'readReceipts', currentUserChat.uid),
          {
            userId: currentUserChat.uid,
            chatroomId,
            lastReadMessageId: latestMessageId ?? null,
            lastReadAt: latestMessageAt,
          },
          { merge: true },
        );
        setReadReceiptsByChatroom((prev) => ({
          ...prev,
          [chatroomId]: {
            ...(prev[chatroomId] ?? {}),
            userId: currentUserChat.uid,
            chatroomId,
            lastReadMessageId: latestMessageId ?? null,
            lastReadAt: latestMessageAt,
          },
        }));
        clearUnreadForChatroom(chatroomId);
      } catch (err) {}
    },
    [currentUserChat?.uid, listDataGroup, listMessage, readReceiptsByChatroom],
  );

  const selectGroupChat = useCallback(
    async (group) => {
      setIsLoadingChatroom(true);
      resetComposerEditState();
      setUserChat(null);
      setGroupChat(group);
      await Read(getChatroomId(group), group?.lastMessageId, group?.lastMessageAt);
    },
    [Read],
  );

  const checkUnread = useCallback(
    (chatroomId) => {
      if (!chatroomId) return false;
      if (!hasLoadedUserUnreads) return false;
      return (unreadsByChatroom[chatroomId] ?? 0) > 0;
    },
    [hasLoadedUserUnreads, unreadsByChatroom],
  );

  const checkGroupUnread = useCallback(
    (items) => {
      return items.some((group) => checkUnread(getChatroomId(group)));
    },
    [checkUnread],
  );

  const seenMessage = useCallback(async () => {
    const activeChat = groupChat ?? userChat;
    await Read(getChatroomId(activeChat), activeChat?.lastMessageId, activeChat?.lastMessageAt);
  }, [groupChat, userChat, Read]);

  const isMessageReadByPrimaryMember = useCallback(
    (messageId: string, chatroomIdArg?: string) => {
      const chatroomId = chatroomIdArg ?? getActiveChatroomId();
      const primaryMemberId = getPrimaryMemberIdByChatroom(chatroomId);
      if (!primaryMemberId || !chatroomId || !messageId) return false;

      const receipt = primaryMemberReadReceiptsByChatroom[chatroomId];
      const lastReadMessageId = receipt?.lastReadMessageId;
      if (!lastReadMessageId) return false;

      const sortedMessages = [...listMessage]
        .filter((message: any) => message.deleted !== true)
        .sort((a: any, b: any) => (a?.createdAt ?? a?.date ?? 0) - (b?.createdAt ?? b?.date ?? 0));
      const msgIndex = sortedMessages.findIndex((m: any) => m.id === messageId);
      const readerIndex = sortedMessages.findIndex((m: any) => m.id === lastReadMessageId);
      return msgIndex >= 0 && readerIndex >= 0 && readerIndex >= msgIndex;
    },
    [groupChat, userChat, listDataGroup, primaryMemberReadReceiptsByChatroom, listMessage],
  );

  return {
    t,
    listUsers,
    handlerChat,
    userChat,
    sendMessage,
    setTextInput,
    textInput,
    listMessage,
    currentUserChat,
    isShowCreateChat,
    setIsShowCreateChat,
    showCreateChat,
    setShowCreateChat,
    handleClose,
    selectGroupChat,
    groupChat,
    listDataGroup,
    listDataGroupSorted,
    listDirectChat,
    isLoading,
    isLoadingChatroom,
    setIsLoading,
    isShowBtn,
    setIsShowBtn,
    deleteMessage,
    editMessage,
    isEdit,
    setIsEdit,
    onCloseEditMessage,
    sendEditMessage,
    checkUnread,
    checkGroupUnread,
    setListDirectChat,
    setListUsers,
    setListDataGroup,
    setListDataGroupSorted,
    listUserNew,
    setListUserNew,
    seenMessage,
    isMessageReadByPrimaryMember,
    loadMoreMessages,
    hasMoreMessages,
    isBumpingMessagePageSize,
    loadMoreGroups,
    hasMoreGroups,
    isLoadingMoreGroups,
  };
};
export default useServices;
